import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { 
  User, 
  Settings, 
  Shield, 
  Heart, 
  LogOut, 
  Menu,
  X,
  Baby,
  GraduationCap,
  Crown
} from 'lucide-react';
import { useAuth } from '../contexts/SupabaseAuthContext';

export const Header: React.FC = () => {
  const { user, logout, showAuthModal } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const location = useLocation();

  const getUserIcon = () => {
    if (!user || user.type === 'guest') return <User className="w-5 h-5" />;
    
    switch (user.type) {
      case 'child': return <Baby className="w-5 h-5 text-pink-500" />;
      case 'parent': return <Heart className="w-5 h-5 text-green-500" />;
      case 'teacher': return <GraduationCap className="w-5 h-5 text-blue-500" />;
      case 'creator': return <Crown className="w-5 h-5 text-purple-500" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  const getUserBadgeColor = () => {
    if (!user || user.type === 'guest') return 'bg-gray-100 text-gray-600';
    
    switch (user.type) {
      case 'child': return 'bg-pink-100 text-pink-700';
      case 'parent': return 'bg-green-100 text-green-700';
      case 'teacher': return 'bg-blue-100 text-blue-700';
      case 'creator': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getWelcomeMessage = () => {
    if (!user || user.type === 'guest') return 'Welcome to CraftyJoy!';
    
    switch (user.type) {
      case 'child': return `Hi ${user.name}! Ready to create?`;
      case 'parent': return `Welcome ${user.name}`;
      case 'teacher': return `Hello ${user.name}`;
      case 'creator': return `Welcome back, ${user.name}`;
      default: return `Welcome ${user.name}`;
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/">
            <motion.div 
              className="flex items-center space-x-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="relative">
                <img 
                  src="/craftyjoy_color_512.png" 
                  alt="CraftyJoy" 
                  className="w-10 h-10 rounded-lg shadow-sm"
                />
                <motion.div
                  className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">CraftyJoy</h1>
                {user && user.type === 'child' && (
                  <div className="flex items-center space-x-1 text-xs text-pink-600">
                    <Shield className="w-3 h-3" />
                    <span>Safe Mode</span>
                  </div>
                )}
              </div>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/editor" 
              className={`text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                location.pathname.startsWith('/editor') ? 'text-purple-600' : ''
              }`}
            >
              Create
            </Link>
            <Link 
              to="/gallery" 
              className={`text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                location.pathname === '/gallery' ? 'text-purple-600' : ''
              }`}
            >
              Gallery
            </Link>
            <Link 
              to="/learn" 
              className={`text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                location.pathname.startsWith('/learn') ? 'text-purple-600' : ''
              }`}
            >
              Learn
            </Link>
            {user && user.type !== 'guest' && (
              <Link 
                to="/dashboard" 
                className={`text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                  location.pathname === '/dashboard' ? 'text-purple-600' : ''
                }`}
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* User Avatar/Menu */}
            <div className="relative">
              <motion.button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 p-2 rounded-xl hover:bg-gray-50 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className={`p-2 rounded-lg ${getUserBadgeColor()}`}>
                  {getUserIcon()}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-gray-900">
                    {user ? user.name || 'Guest' : 'Sign In'}
                  </div>
                  {user && (
                    <div className="text-xs text-gray-500 capitalize">
                      {user.type} Account
                    </div>
                  )}
                </div>
              </motion.button>

              {/* User Dropdown Menu */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 py-2 z-50"
                  >
                    {user ? (
                      <>
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="text-sm font-medium text-gray-900">
                            {getWelcomeMessage()}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {user.email}
                          </div>
                        </div>

                        <div className="px-2 py-2">
                          <button className="w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                            <Settings className="w-4 h-4 text-gray-600" />
                            <span className="text-sm text-gray-700">Settings</span>
                          </button>
                          
                          {(user.type === 'parent' || user.type === 'teacher') && (
                            <button className="w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                              <Shield className="w-4 h-4 text-gray-600" />
                              <span className="text-sm text-gray-700">Safety Controls</span>
                            </button>
                          )}

                          <button 
                            onClick={() => {
                              logout();
                              setShowUserMenu(false);
                            }}
                            className="w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-red-50 transition-colors text-red-600"
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Sign Out</span>
                          </button>
                        </div>
                      </>
                                          ) : (
                        <div className="px-2 py-2">
                          <button 
                            onClick={() => {
                              showAuthModal();
                              setShowUserMenu(false);
                            }}
                            className="w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-purple-50 transition-colors text-purple-600"
                          >
                            <User className="w-4 h-4" />
                            <span className="text-sm">Sign In</span>
                          </button>
                        </div>
                      )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden border-t border-gray-200 bg-white"
            >
              <div className="px-4 py-4 space-y-4">
                <Link 
                  to="/editor" 
                  className={`block text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                    location.pathname.startsWith('/editor') ? 'text-purple-600' : ''
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  Create
                </Link>
                <Link 
                  to="/gallery" 
                  className={`block text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                    location.pathname === '/gallery' ? 'text-purple-600' : ''
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  Gallery
                </Link>
                <Link 
                  to="/learn" 
                  className={`block text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                    location.pathname.startsWith('/learn') ? 'text-purple-600' : ''
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  Learn
                </Link>
                {user && user.type !== 'guest' && (
                  <Link 
                    to="/dashboard" 
                    className={`block text-gray-600 hover:text-purple-600 font-medium transition-colors ${
                      location.pathname === '/dashboard' ? 'text-purple-600' : ''
                    }`}
                    onClick={() => setShowMobileMenu(false)}
                  >
                    Dashboard
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};